/**
 * SuggestChangeButton — universal "Suggest a change" launcher.
 *
 * Tiny header pill that opens a modal with subject + body fields and
 * POSTs to /api/suggestions. Any signed-in user can submit. The new
 * row lands in the admin "Requested changes" queue as `pending`.
 *
 * iter150e — supports attaching non-executable files (PDF, images,
 * video, Office docs, CSV, TXT, ZIP). Executable / script files are
 * rejected client-side AND server-side.
 */
import { useEffect, useState } from "react";
import { Lightbulb, X, PaperPlaneTilt, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";

const ATTACH_ALLOWED_EXT = [
  ".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".heic", ".heif",
  ".bmp", ".tiff", ".mp4", ".mov", ".webm", ".m4v",
  ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".odt", ".ods", ".odp",
  ".csv", ".txt", ".md", ".rtf", ".log", ".zip",
];
const ATTACH_BLOCKED_EXT = [
  ".exe", ".bat", ".cmd", ".com", ".scr", ".msi", ".ps1", ".vbs", ".vbe",
  ".js", ".mjs", ".cjs", ".jse", ".wsf", ".wsh", ".jar", ".class",
  ".sh", ".bash", ".zsh", ".fish", ".csh", ".ksh",
  ".py", ".pyc", ".pyo", ".pyd", ".rb", ".pl", ".php", ".phtml", ".cgi",
  ".dll", ".so", ".dylib", ".app", ".apk", ".ipa", ".deb", ".rpm",
  ".reg", ".inf", ".lnk", ".url", ".scf", ".chm", ".hta",
];

export default function SuggestChangeButton({ className = "", iconOnly = false, hideTrigger = false, openSignal = 0 }) {
  const t = useT();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  // Allow a parent (e.g. TopBarToolsMenu) to open this modal without
  // showing the standalone pill — pass `hideTrigger` + bump `openSignal`.
  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  const stageFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      const lower = (f.name || "").toLowerCase();
      const dot = lower.lastIndexOf(".");
      const ext = dot >= 0 ? lower.slice(dot) : "";
      if (!ext) { toast.error(t("suggest.file_no_ext", `"${f.name}" has no extension.`)); continue; }
      if (ATTACH_BLOCKED_EXT.includes(ext)) {
        toast.error(t("suggest.file_blocked", `"${f.name}" is an executable / script and cannot be attached.`));
        continue;
      }
      if (!ATTACH_ALLOWED_EXT.includes(ext)) {
        toast.error(t("suggest.file_unsupported", `"${f.name}" — file type ${ext} is not supported.`));
        continue;
      }
      if (f.size > 25 * 1024 * 1024) {
        toast.error(t("suggest.file_too_big", `"${f.name}" exceeds 25 MB.`));
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted].slice(0, 10));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || subject.trim().length < 3) {
      toast.error(t("suggest.subject_too_short", "Add a short subject (3+ chars)."));
      return;
    }
    if (!body.trim() || body.trim().length < 5) {
      toast.error(t("suggest.body_too_short", "Add a few words describing the change."));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/suggestions", { subject: subject.trim(), body: body.trim(), lang });
      const sid = r.data?.id;
      if (sid && files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f, f.name));
        try {
          await api.post(`/suggestions/${encodeURIComponent(sid)}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uErr) {
          toast.error(uErr?.response?.data?.detail || t("suggest.attach_failed", "Files could not be attached."));
        }
      }
      toast.success(t("suggest.thanks", "Thanks! Your request is in the admin queue."));
      setSubject(""); setBody(""); setFiles([]); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("suggest.failed", "Could not submit — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          data-testid="suggest-change-launcher"
          onClick={() => setOpen(true)}
          title={t("suggest.tooltip", "Suggest a change to the portal")}
          className={`inline-flex items-center justify-center gap-1 w-8 h-8 sm:w-auto sm:px-2 rounded-sm border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wider hover:bg-amber-100 ${className}`}
        >
          <Lightbulb size={11} weight="fill" />
          {!iconOnly && (
            <span className="hidden sm:inline">{t("suggest.button", "Suggest a change")}</span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/30 p-4 pt-16"
             data-testid="suggest-change-modal" onClick={() => !busy && setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb size={16} weight="fill" className="text-amber-600" />
              <h2 className="text-sm font-bold tracking-tight">{t("suggest.title", "Suggest a change")}</h2>
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                      data-testid="suggest-change-close"
                      className="ml-auto text-slate-400 hover:text-slate-700">
                <X size={14} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {t("suggest.subtitle", "Tell us what would make this portal better. Admins triage every request.")}
            </p>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("suggest.subject_label", "What's the change?")}
              </span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("suggest.subject_placeholder", "e.g. Print warranty list to PDF")}
                maxLength={200}
                data-testid="suggest-subject"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("suggest.body_label", "Why does it matter?")}
              </span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t("suggest.body_placeholder", "Describe the problem you hit and how this change would help.")}
                maxLength={3000}
                rows={4}
                data-testid="suggest-body"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none resize-y"
              />
            </label>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("suggest.attach_label", "Attach files (optional)")}
              </span>
              <label htmlFor="suggest-file-input"
                     data-testid="suggest-attach-trigger"
                     className="inline-flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                <Paperclip size={11} weight="bold" />
                {t("suggest.attach_button", "Choose files")}
              </label>
              <input
                id="suggest-file-input" type="file" multiple
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }}
                data-testid="suggest-files"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif,.bmp,.tiff,.mp4,.mov,.webm,.m4v,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.csv,.txt,.md,.rtf,.log,.zip"
              />
              <p className="mt-1 text-[10px] text-slate-500">
                {t("suggest.attach_hint", "Up to 10 files, 25 MB each. PDFs, images, video, Office docs, CSV, TXT, ZIP. Executable / script files are not allowed.")}
              </p>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="suggest-staged-files">
                  {files.map((f, i) => (
                    <li key={i}
                        className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 text-[11px]">
                      <Paperclip size={10} weight="bold" className="text-slate-500" />
                      <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                      <span className="text-slate-400 tabular-nums">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button"
                              onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                              data-testid={`suggest-remove-file-${i}`}
                              className="text-rose-600 hover:text-rose-800">
                        <X size={11} weight="bold" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50">
                {t("suggest.cancel", "Cancel")}
              </button>
              <button type="submit" disabled={busy}
                      data-testid="suggest-submit"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
                <PaperPlaneTilt size={11} weight="fill" />
                {busy ? t("suggest.submitting", "Submitting…") : t("suggest.submit", "Send to admins")}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
